import { Injectable, inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { NetworkService } from '@angular-workspace/core';
import { AclApdApiService, parsePolicyExpiryIsoToMillis } from '@angular-workspace/services';
import { environment } from '../../../environments/environment';
import { WorkspaceContextService } from '../../services/workspace-context.service';
import {
  ConversationMessage,
  ConversationRole,
} from '../components/conversation/conversation.component';
import { readAccessRequestPurpose } from '../utils/access-request-purpose';

type SenderRole = 'requester' | 'approver';

/** Message returned by the request conversation APIs. */
interface RequestConversationMessage {
  id: string;
  parent_msg_id: string | null;
  sender_role: SenderRole;
  content: string;
  is_internal: boolean;
  message_type?: string;
  created_at: string;
  replies?: { result: RequestConversationMessage[] };
}

/** API maximum page size, so the whole thread loads in one call. */
const CONVERSATION_PAGE_SIZE = 1000;
/** `request_type` sent when starting a conversation on an asset access request. */
const CONVERSATION_REQUEST_TYPE = 'join_orgs';
const READ_ONLY_CONVERSATION_STATUSES = new Set([
  'withdrawn',
  'granted',
  'approved',
  'active',
  'revoked',
  'expired',
]);

/** Withdrawn and approved threads stay visible, but nobody can add or change messages. */
export function isConversationReadOnly(status: string | undefined): boolean {
  return READ_ONLY_CONVERSATION_STATUSES.has((status ?? '').trim().toLowerCase());
}

/** Approved access stays open until the policy expiry has passed. */
export function isAccessStillActive(expiry: string | undefined): boolean {
  const raw = (expiry ?? '').trim();
  if (!raw || raw === '-') {
    return true;
  }
  const millis = parsePolicyExpiryIsoToMillis(raw);
  return !millis || millis > Date.now();
}

/**
 * Pending and rejected threads stay editable.
 * Withdrawn, approved, revoked, and expired threads stay available and are read-only.
 * An approved thread stays available only until the policy expiry has passed; after that it is expired.
 */
export function canOpenConversation(status: string | undefined, expiry?: string): boolean {
  const normalized = (status ?? '').trim().toLowerCase();
  if (
    normalized === 'pending' ||
    normalized === 'rejected' ||
    normalized === 'withdrawn' ||
    normalized === 'revoked' ||
    normalized === 'expired'
  ) {
    return true;
  }
  if (normalized === 'granted' || normalized === 'approved' || normalized === 'active') {
    return isAccessStillActive(expiry);
  }
  return false;
}

/**
 * Conversation Service for request conversation API calls.
 * Threads are cached per `${role}:${requestId}` so a new message can reply to the other party's latest thread.
 * This service uses NetworkService to make HTTP requests
 */
const REQUEST_ID_KEYS = ['id', 'requestId', 'request_id', 'accessRequestId', 'access_request_id'];

@Injectable({ providedIn: 'root' })
export class ConversationService {
  private readonly networkService = inject(NetworkService);
  private readonly keycloak = inject(KeycloakService);
  private readonly workspaceContext = inject(WorkspaceContextService);
  private readonly aclApdApi = inject(AclApdApiService);
  private readonly conversations = new Map<string, ConversationMessage[]>();
  /** Note ids created in this session, so a refetch that omits the flag still excludes them from the send limit. */
  private readonly ownInternalNoteIds = new Set<string>();

  /**
   * A private note, not a message shared with the other participant.
   * The API may return the flag as a boolean, a string, or only via message type.
   */
  private isInternalMessage(message: RequestConversationMessage): boolean {
    const raw = message as unknown as Record<string, unknown>;
    const flag = raw['is_internal'] ?? raw['isInternal'] ?? raw['internal'];
    if (flag === true || flag === 1) return true;
    if (typeof flag === 'string') {
      const normalized = flag.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
        return true;
      }
    }
    const type = String(raw['message_type'] ?? raw['messageType'] ?? '')
      .trim()
      .toLowerCase();
    return type === 'note' || type === 'internal' || type === 'internal_note';
  }

  /**
   * Purpose originally entered on the requester's access request.
   * Granted Access opens the thread from the approved policy, which may omit that text,
   * so look it up on the granted access request with the same id.
   */
  async findRequesterPurpose(requestId: string): Promise<string> {
    const target = requestId.trim().toLowerCase();
    if (!target) return '';
    try {
      const pageSize = 100;
      let page = 1;
      let total = Number.POSITIVE_INFINITY;
      while ((page - 1) * pageSize < total && page <= 10) {
        const response = await this.aclApdApi.getUserAccessRequests(page, pageSize, 'GRANTED');
        const items = listRecords(response);
        for (const item of items) {
          if (recordIds(item).some((id) => id.toLowerCase() === target)) {
            return readAccessRequestPurpose(item);
          }
        }
        total = listTotal(response, items.length);
        if (items.length < pageSize) break;
        page += 1;
      }
    } catch (error) {
      console.error('Error loading access request purpose:', error);
    }
    return '';
  }

  /**
   * Whether the logged-in user owns the requested asset; on the owner side only the asset owner gets the conversation.
   * Delegate sessions act as the delegator, the same as the `did` header.
   * @param ownerId `owner.ownerId` from the access request
   * @returns True when the user is the asset owner
   */
  isAssetOwner(ownerId: string | undefined): boolean {
    const userId = this.workspaceContext.isDelegateSession()
      ? this.workspaceContext.getActiveDelegatorId()
      : this.keycloak.getKeycloakInstance()?.tokenParsed?.sub;
    return !!ownerId && ownerId === userId;
  }

  /**
   * Get all messages for a request (root messages with nested replies), oldest first.
   * GET {basePathUrl}{conversationApiPath}{requestId}/conversations?page=1&size=1000
   * @param role Whether the current user is the requester or the asset owner
   * @param requestId Request id
   * @returns Promise with the flattened conversation messages
   */
  async getMessages(
    role: ConversationRole,
    requestId: string,
  ): Promise<ConversationMessage[]> {
    try {
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('size', CONVERSATION_PAGE_SIZE.toString());

      const response = await this.networkService.get_api(
        `${environment.basePathUrl}${environment.conversationApiPath}${requestId}/conversations?${params.toString()}`,
        undefined,
        false, // disableToaster
      );

      const ownSenderRole: SenderRole =
        role === 'owner' ? 'approver' : 'requester';
      // Replies can come both nested under their parent and as top-level items; keep one per id.
      const uniqueMessages = new Map(
        ((response?.result ?? []) as RequestConversationMessage[])
          .flatMap((message) => [message, ...(message.replies?.result ?? [])])
          .map((message) => [message.id, message]),
      );
      const messages = [...uniqueMessages.values()]
        .filter(
          (message) =>
            !this.isInternalMessage(message) ||
            message.sender_role === ownSenderRole,
        )
        .map(
          (message): ConversationMessage => ({
            id: message.id,
            parentMessageId: message.parent_msg_id ?? message.id,
            author: message.sender_role === ownSenderRole ? 'me' : 'other',
            text: message.content,
            createdAt: message.created_at,
            internal:
              this.isInternalMessage(message) ||
              this.ownInternalNoteIds.has(message.id),
          }),
        )
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      this.conversations.set(`${role}:${requestId}`, messages);
      return messages;
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      throw error;
    }
  }

  /**
   * Create a message. Replies to the other party's latest thread, or creates a root message when they have not written yet.
   * POST {basePathUrl}{conversationApiPath}{requestId}/conversations/{parentMessageId}/reply
   * POST {basePathUrl}{conversationApiPath}{requestId}/conversations
   * @param role Whether the current user is the requester or the asset owner
   * @param requestId Request id
   * @param content Message text
   * @param isInternal Whether the message is an internal note
   * @returns Promise with the refreshed conversation messages
   */
  async createMessage(
    role: ConversationRole,
    requestId: string,
    content: string,
    isInternal: boolean,
  ): Promise<ConversationMessage[]> {
    try {
      const cacheKey = `${role}:${requestId}`;
      const previousIds = new Set(
        (this.conversations.get(cacheKey) ?? []).map((message) => message.id),
      );
      const url = `${environment.basePathUrl}${environment.conversationApiPath}${requestId}/conversations`;
      const lastReceivedMessage = (this.conversations.get(cacheKey) ?? [])
        .filter((message) => message.author === 'other' && !message.internal)
        .pop();
      const payload = {
        sender_role: role === 'owner' ? 'approver' : 'requester',
        message_type: isInternal ? 'note' : 'text',
        content,
        is_internal: isInternal,
      };

      if (lastReceivedMessage) {
        await this.networkService.post_api(
          `${url}/${lastReceivedMessage.parentMessageId}/reply`,
          payload,
        );
      } else {
        await this.networkService.post_api(url, {
          ...payload,
          parent_msg_id: null,
          request_type: CONVERSATION_REQUEST_TYPE,
        });
      }

      const messages = await this.getMessages(role, requestId);
      if (!isInternal) return messages;
      for (const message of messages) {
        if (message.author === 'me' && !previousIds.has(message.id)) {
          this.ownInternalNoteIds.add(message.id);
          message.internal = true;
        }
      }
      this.conversations.set(cacheKey, messages);
      return messages;
    } catch (error) {
      console.error('Error creating conversation message:', error);
      throw error;
    }
  }

  /**
   * Update the content of an own message.
   * PUT {basePathUrl}{conversationApiPath}{requestId}/conversations/{messageId}
   * @param role Whether the current user is the requester or the asset owner
   * @param requestId Request id
   * @param messageId Message id
   * @param content Updated message text
   * @returns Promise with the refreshed conversation messages
   */
  async updateMessage(
    role: ConversationRole,
    requestId: string,
    messageId: string,
    content: string,
  ): Promise<ConversationMessage[]> {
    try {
      await this.networkService.put_api(
        `${environment.basePathUrl}${environment.conversationApiPath}${requestId}/conversations/${messageId}`,
        { content },
      );
      return this.getMessages(role, requestId);
    } catch (error) {
      console.error('Error updating conversation message:', error);
      throw error;
    }
  }

  /**
   * Delete an own message.
   * DELETE {basePathUrl}{conversationApiPath}{requestId}/conversations/{messageId}
   * @param role Whether the current user is the requester or the asset owner
   * @param requestId Request id
   * @param messageId Message id
   * @returns Promise with the refreshed conversation messages
   */
  async deleteMessage(
    role: ConversationRole,
    requestId: string,
    messageId: string,
  ): Promise<ConversationMessage[]> {
    try {
      await this.networkService.delete_api(
        `${environment.basePathUrl}${environment.conversationApiPath}${requestId}/conversations/${messageId}`,
      );
      return this.getMessages(role, requestId);
    } catch (error) {
      console.error('Error deleting conversation message:', error);
      throw error;
    }
  }
}

function listRecords(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) {
    return response.filter(isRecord);
  }
  if (!isRecord(response)) return [];
  const result = response['result'];
  if (Array.isArray(result)) return result.filter(isRecord);
  const data = response['data'];
  if (Array.isArray(data)) return data.filter(isRecord);
  return [];
}

function listTotal(response: unknown, fallback: number): number {
  if (!isRecord(response)) return fallback;
  const pagination = response['paginationInfo'];
  const total = isRecord(pagination) ? pagination['totalCount'] : response['totalCount'];
  const count = Number(total);
  return Number.isFinite(count) && count >= 0 ? count : fallback;
}

function recordIds(item: Record<string, unknown>): string[] {
  return REQUEST_ID_KEYS.flatMap((key) => {
    const value = item[key];
    return typeof value === 'string' && value.trim() ? [value.trim()] : [];
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
